import * as THREE from 'three';
import { bytesOf, floatsOf, indicesOf, nodeMatrix, readGlb } from './glb';
import { hash3 } from './fishSwim';
import { CORAL_RATES, episodeKilled } from '../sim/coral';
import { REEF_ISLANDS, binFloorsKm2, stratumIndex, type Reef } from '../sim/reef';
import type { CoralState } from '../sim/types';
import { PALE_SHARE } from '../sim/data/reef/paleShare';
import { FishBatch, SOLID_STAND, fishAtlas, type FishDraw } from './fishBatch';
import type { FogUniforms } from './skin';
import { CORAL_SPRITES } from '../ui/data/coralSprites';
import { LIMU_SPRITES } from '../ui/data/limuSprites';
import type { Sprite } from '../ui/data/spriteTypes';

/**
 * The reef as outcrops of rock and coral (M87, the user's word: "the reef
 * looks messy. create a glb 3d object for the reef. use pixel art sprites as
 * its skin"; their choices: reef outcrops, every object growing with the
 * zoom, and their own model; since M110 their Hawaiian reef kit,
 * `art/hawaii-reef/`, built for the game by `scripts/reefModel.ts` into
 * `models/reef.glb`, the user's word: "use it for the sprites for both
 * aquarium and habitat views"). Its outcrops are set on each island's reef as
 * many as its floor (`KM2_PER_OUTCROP`), each tilted toward its floor's slope;
 * on each, its colonies shown as its column's coral is (`colonyLook`): a share
 * of them as its cover is of the ceiling the reef can hold
 * (`CORAL_RATES.ceilingPercent`, as the reef's tint is), each by its own draw,
 * so a colony comes and goes only as the cover crosses it; pale, then white,
 * as the column's coral bleaches, each by another; and those a heat episode
 * killed standing dead until it ends. All the outcrops' rock is one mesh (or
 * one a chunk); the colonies the user's coral sprites since M164, one batch
 * (`ColonySprites`).
 */

/** A part of the model: its corners' positions, normals and UVs, three to a triangle. */
export interface Part {
  position: Float32Array;
  normal: Float32Array;
  uv: Float32Array;
}

/**
 * How a colony looks, each the kit's atlas of its cells (M110): living in its colors; pale (the kit's stressed, "Living
 * but moderately pale"); bleached ("Living coral with severe pigmentation loss"); dead, its bare skeleton ("Dead exposed
 * calcium carbonate skeleton"), the kit's batch 5's README's words. Its recovering and overgrown looks are not drawn:
 * the simulation holds no colony recovering or overgrown.
 */
export const CORAL_LOOKS = ['healthy', 'pale', 'bleached', 'dead'] as const;
export type CoralLook = (typeof CORAL_LOOKS)[number];

/** Each look's material in the model (`scripts/reefModel.ts`). */
const LOOK_MATERIALS: Readonly<Record<CoralLook, string>> = { healthy: 'coral', pale: 'coral pale', bleached: 'coral bleached', dead: 'coral dead' };

export interface ReefModel {
  /** Each outcrop's rock (its pieces that are not coral, as one), its colonies' forms and matrices on it, and its width across, m. */
  outcrops: { rock: Part; slots: { form: string; matrix: THREE.Matrix4 }[]; widthM: number }[];
  /** Each coral form's shape, by its model's id. */
  forms: ReadonlyMap<string, Part>;
  /** Each form's species. */
  species: ReadonlyMap<string, string>;
  /** Each piece that is not coral (rock, rubble, turf, coralline, sponge), by its model's id, its shape and its kind. */
  pieces: ReadonlyMap<string, { part: Part; kind: string }>;
  /** The rock's skin and each look's of the coral (the kit's, read by the game for its rock alone since M164), PNG, as the model carries them, and whether each is drawn on both sides of its faces. */
  images: { rock: Uint8Array; looks: Readonly<Record<CoralLook, Uint8Array>> };
  doubleSided: { rock: boolean; corals: boolean };
}

/** The tallest of a model's outcrops with its colonies on it, each as tall as its sprite (M164), m above its foot. */
export function tallestM(model: ReefModel): number {
  const v = new THREE.Vector3();
  let top = 0;
  for (const o of model.outcrops) {
    for (let i = 1; i < o.rock.position.length; i += 3) top = Math.max(top, o.rock.position[i] as number);
    for (const slot of o.slots) {
      const part = model.forms.get(slot.form);
      if (!part) continue;
      // Each colony as tall as its sprite stands (M164, `colonyStretch`).
      const m = slot.matrix.clone().multiply(stretchedForm(slot.form, part));
      for (let i = 0; i < part.position.length; i += 3) top = Math.max(top, v.fromArray(part.position, i).applyMatrix4(m).y);
    }
  }
  return top;
}

/** Km² of reef floor (to 30 m) to an outcrop (JUDGED: Maui Nui's 410 km² come to 26, MEASURED by the test). */
export const KM2_PER_OUTCROP = 16;

/** Parts laid as one, each by its matrix (its normals by the matrix's normal matrix). */
export function laidPart(parts: readonly { part: Part; matrix: THREE.Matrix4 }[]): Part {
  const corners = parts.reduce((n, p) => n + p.part.position.length / 3, 0);
  const out: Part = { position: new Float32Array(corners * 3), normal: new Float32Array(corners * 3), uv: new Float32Array(corners * 2) };
  const v = new THREE.Vector3();
  let at = 0;
  for (const { part, matrix } of parts) {
    const normalMatrix = new THREE.Matrix3().getNormalMatrix(matrix);
    for (let i = 0; i < part.position.length / 3; i++, at++) {
      v.fromArray(part.position, i * 3).applyMatrix4(matrix).toArray(out.position, at * 3);
      v.fromArray(part.normal, i * 3).applyMatrix3(normalMatrix).normalize().toArray(out.normal, at * 3);
      out.uv[at * 2] = part.uv[i * 2] as number;
      out.uv[at * 2 + 1] = part.uv[i * 2 + 1] as number;
    }
  }
  return out;
}

/** The reef's model read from its GLB's bytes. */
export function reefModelOf(bytes: Uint8Array): ReefModel {
  const glb = readGlb(bytes);
  const { json } = glb;
  const parts = new Map<number, Part>();
  const partOf = (mesh: number): Part => {
    const known = parts.get(mesh);
    if (known) return known;
    const primitive = json.meshes[mesh]?.primitives[0];
    if (!primitive || (primitive.mode ?? 4) !== 4) throw new Error(`mesh ${mesh} is not triangles`);
    const { POSITION, NORMAL, TEXCOORD_0 } = primitive.attributes;
    if (POSITION === undefined || NORMAL === undefined || TEXCOORD_0 === undefined) throw new Error(`mesh ${mesh} lacks a position, normal or UV`);
    const [p, n, t] = [floatsOf(glb, POSITION), floatsOf(glb, NORMAL), floatsOf(glb, TEXCOORD_0)];
    // An indexed mesh's corners copied out to each triangle that shares them, three to a triangle, as the outcrops lay them.
    const indices = primitive.indices === undefined ? null : indicesOf(glb, primitive.indices);
    const pick = (from: Float32Array, width: number): Float32Array => {
      if (!indices) return from;
      const out = new Float32Array(indices.length * width);
      indices.forEach((i, k) => out.set(from.subarray(i * width, (i + 1) * width), k * width));
      return out;
    };
    const part = { position: pick(p, 3), normal: pick(n, 3), uv: pick(t, 2) };
    parts.set(mesh, part);
    return part;
  };
  const forms = new Map<string, Part>();
  const species = new Map<string, string>();
  const pieces = new Map<string, { part: Part; kind: string }>();
  const outcrops = (json.scenes[json.scene ?? 0]?.nodes ?? []).map((index) => {
    const children = (json.nodes[index]?.children ?? []).map((c) => json.nodes[c] as NonNullable<(typeof json.nodes)[number]>);
    // Its rock: every piece of it that is not a colony, each where it lies.
    const rockNodes = children.filter((c) => c.mesh !== undefined && typeof c.extras?.form !== 'string');
    if (rockNodes.length === 0) throw new Error(`outcrop ${index} has no rock`);
    const rock = laidPart(rockNodes.map((c) => {
      const part = partOf(c.mesh as number);
      pieces.set(json.meshes[c.mesh as number]?.name ?? `mesh ${c.mesh}`, { part, kind: typeof c.extras?.piece === 'string' ? c.extras.piece : 'rock' });
      return { part, matrix: new THREE.Matrix4().fromArray(nodeMatrix(c)) };
    }));
    const slots = children.filter((c) => typeof c.extras?.form === 'string' && c.mesh !== undefined).map((c) => {
      const form = c.extras?.form as string;
      if (!forms.has(form)) forms.set(form, partOf(c.mesh as number));
      if (typeof c.extras?.species === 'string') species.set(form, c.extras.species);
      return { form, matrix: new THREE.Matrix4().fromArray(nodeMatrix(c)) };
    });
    let [minX, maxX, minZ, maxZ] = [Infinity, -Infinity, Infinity, -Infinity];
    for (let i = 0; i < rock.position.length; i += 3) {
      minX = Math.min(minX, rock.position[i] as number);
      maxX = Math.max(maxX, rock.position[i] as number);
      minZ = Math.min(minZ, rock.position[i + 2] as number);
      maxZ = Math.max(maxZ, rock.position[i + 2] as number);
    }
    return { rock, slots, widthM: Math.max(maxX - minX, maxZ - minZ) };
  });
  // Each material's picture, by its base color's texture, and its sides.
  const material = (name: string): NonNullable<(typeof json.materials)>[number] => {
    const found = json.materials?.find((m) => m.name === name);
    if (!found) throw new Error(`the model has no ${name} material`);
    return found;
  };
  const image = (name: string): Uint8Array => {
    const texture = json.textures?.[material(name).pbrMetallicRoughness?.baseColorTexture?.index ?? -1];
    const found = json.images?.[texture?.source ?? -1];
    if (!found) throw new Error(`the ${name} material has no picture`);
    return bytesOf(glb, found.bufferView);
  };
  const looks = Object.fromEntries(CORAL_LOOKS.map((look) => [look, image(LOOK_MATERIALS[look])])) as Record<CoralLook, Uint8Array>;
  return {
    outcrops,
    forms,
    species,
    pieces,
    images: { rock: image('rock'), looks },
    doubleSided: { rock: material('rock').doubleSided === true, corals: material('coral').doubleSided === true },
  };
}

/**
 * The share of the colonies the simulation's "pale or bleached" holds that are drawn pale, not white: the lowest of
 * their draws white, the next this share of them pale, so a colony pales before it whitens and colors again after.
 * MEASURED at Maui Nui (M113, the user's word: "carry with citation"): NOAA's towed divers off Maui and Lānaʻi in
 * November 2015, 59% of the pale or bleached coral pale (`src/sim/data/reef/paleShare.ts`, from NCEI 0157624 by
 * `scripts/data/towedBleaching.ts`), where M110 drew Kāneʻohe Bay's 36% (Bahr et al. 2017, the survey the
 * simulation's bleaching is set against, `coral.ts`: 46% pale or bleached, p. 5, 29.58% white, Table 4, p. 10).
 */
export { PALE_SHARE };

/**
 * A colony's look, or null where none stands, by its two fixed draws, each 0 to 1: standing while `shown` is under
 * `share`, the share of the full cover its place's cover is (the reef's ceiling, or Maui Nui Reef's the cover all its
 * colonies make, M125); of those standing, the ones whose `white` is under
 * the share bleached, pale or white (`PALE_SHARE`); and standing dead while `shown` is under the share before the heat
 * episode under way killed `killedPercent` of it (`episodeKilled`, which holds what an episode killed until it ends),
 * so the colonies it killed are the ones that stood last, and stand as skeletons.
 */
export function colonyLook(shown: number, white: number, share: number, bleachedPercent: number, killedPercent: number): CoralLook | null {
  if (shown < share) {
    const bleached = bleachedPercent / 100;
    if (white < bleached * (1 - PALE_SHARE)) return 'bleached';
    return white < bleached ? 'pale' : 'healthy';
  }
  const killed = Math.min(0.99, Math.max(0, killedPercent / 100));
  return shown < Math.min(1, share / (1 - killed)) ? 'dead' : null;
}

/**
 * Each of the reef's columns' coral a heat episode under way has killed, percent of its coral before the episode
 * (in `reef.columns` order): each of its own island's strata's coral before it taken back from its cover now by its
 * band's killed share (`episodeKilled`), weighted by the column's floor in the band; 0 between episodes.
 */
export function columnKilled(reef: Reef, c: CoralState): number[] {
  const byBin = episodeKilled(reef, c).byBin.map((x) => x / 100);
  const bins = binFloorsKm2(reef);
  return reef.columns.map((_, j) => {
    let [before, killed] = [0, 0];
    bins.forEach((b, k) => {
      const lost = byBin[k] ?? 0;
      const x = ((c.coverPercent[stratumIndex(reef.island[j] as number, k)] as number) * (b[j] as number)) / (1 - lost);
      before += x;
      killed += x * lost;
    });
    return before > 0 ? (100 * killed) / before : 0;
  });
}

/** A part as a geometry of its own. */
export function partGeometry(part: Part): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(part.position, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(part.normal, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(part.uv, 2));
  return g;
}

/** Which of the user's coral sprites a form of the kit draws (M164): its coral by its name (`hi_coral_lobe_a` the lobe coral's). */
export function coralOfForm(form: string): keyof typeof CORAL_SPRITES | null {
  return (Object.keys(CORAL_SPRITES) as (keyof typeof CORAL_SPRITES)[]).find((k) => form.includes(`_${k}_`)) ?? null;
}

/**
 * Which of its coral's colonies, the user's variants of it, a form of the kit draws (M164): by its letter in turn
 * (`hi_coral_lobe_b` the lobe coral's second, its `_c` its first again while it has two).
 */
export function variantOfForm(form: string, variants: number): number {
  const letter = /_([a-z])$/.exec(form)?.[1];
  return letter ? (letter.charCodeAt(0) - 97) % variants : 0;
}

/** A part's span across, model meters: the wider of its x and z extents. */
export function spanOf(part: Part): number {
  let [x0, x1, z0, z1] = [Infinity, -Infinity, Infinity, -Infinity];
  for (let k = 0; k < part.position.length; k += 3) {
    x0 = Math.min(x0, part.position[k] as number);
    x1 = Math.max(x1, part.position[k] as number);
    z0 = Math.min(z0, part.position[k + 2] as number);
    z1 = Math.max(z1, part.position[k + 2] as number);
  }
  return Math.max(x1 - x0, z1 - z0);
}

/** The sprites a form of the kit draws (M164): its coral's, the variant its letter gives (`variantOfForm`); null for a form of none. */
function colonyOf(form: string): Readonly<Record<CoralLook, Sprite>> | null {
  const coral = coralOfForm(form);
  if (!coral) return null;
  const colonies = CORAL_SPRITES[coral];
  return colonies[variantOfForm(form, colonies.length)] ?? null;
}

/**
 * How much taller a form's colony is drawn than its 3D shape stands (M164's review: the height fields, the whole tank's
 * clearance under the surface and the outcrops' tallest had read the 3D shapes, a lobe coral's drawn 1.49 m where its
 * shape stood 0.33): its sprite's height at its span's width over its shape's top (all four looks one box); 1 for a form
 * of none.
 */
function colonyStretch(form: string, part: Part): number {
  const sprite = colonyOf(form)?.healthy;
  let top = 0;
  for (let k = 1; k < part.position.length; k += 3) top = Math.max(top, part.position[k] as number);
  return sprite && top > 0 ? (spanOf(part) * sprite.height) / sprite.width / top : 1;
}

/** A form's shape as its colony's sprite stands (`colonyStretch`): stretched up from its base, at y = 0. */
function stretchedForm(form: string, part: Part): THREE.Matrix4 {
  return new THREE.Matrix4().makeScale(1, colonyStretch(form, part), 1);
}

/** A colony's footprint, `widthM` across at a point: its middle and two rings of eight, at a half and the whole of its half-width (x and z in twos; M175). */
export function footprint(x: number, z: number, widthM: number): number[] {
  const r = widthM / 2;
  const out = [x, z];
  for (const f of [0.5, 1]) {
    for (let j = 0; j < 8; j++) out.push(x + f * r * Math.cos((j * Math.PI) / 4), z + f * r * Math.sin((j * Math.PI) / 4));
  }
  return out;
}

/** A colony's footprint's ground (`footprint`), each point at `groundAt`'s height there (x, y, z in threes). */
function footprintGround(x: number, z: number, widthM: number, groundAt: (x: number, z: number) => number): Float32Array {
  const at = footprint(x, z, widthM);
  const out = new Float32Array((at.length / 2) * 3);
  for (let k = 0; k < at.length; k += 2) out.set([at[k] as number, groundAt(at[k] as number, at[k + 1] as number), at[k + 1] as number], (k / 2) * 3);
  return out;
}

/**
 * Every coral's sprites in every look, and the limu's (M243), in one atlas (`fishAtlas`), one for every set of colonies drawn (M182: the whole
 * tank's view is kept while a habitat is open, and its set and the Reef's had each built one, 302 by 1,996 pixels, about
 * 2.4 MB before its mipmaps, MEASURED by review 26's reviewer; the eight limu's 24 sprites since M244, 507 by 1,996, 4.05 MB, MEASURED by
 * codebase review 29's reviewer with `fishAtlas` over the same list, the whole tank's set carrying them though it draws
 * none), and how many sets hold it: freed when the last
 * lets it go (`releaseCoralAtlas`), built again by the next to want it.
 */
let coralAtlas: { atlas: ReturnType<typeof fishAtlas>; holders: number } | null = null;

/** The corals' atlas, held by one more set (`coralAtlas`). */
function takeCoralAtlas(): ReturnType<typeof fishAtlas> {
  coralAtlas ??= { atlas: fishAtlas([...Object.values(CORAL_SPRITES).flatMap((colonies) => colonies.flatMap((c) => CORAL_LOOKS.map((look) => c[look]))), ...Object.values(LIMU_SPRITES).flat()]), holders: 0 };
  coralAtlas.holders++;
  return coralAtlas.atlas;
}

/** The corals' atlas let go by a set, and freed where none holds it now. */
function releaseCoralAtlas(): void {
  if (!coralAtlas || --coralAtlas.holders > 0) return;
  coralAtlas.atlas.texture.dispose();
  coralAtlas = null;
}

/**
 * The colonies drawn as the user's pixel-art sprites (M164, the user's word: "i am also considering changing the 3d
 * objects to towering reef rock and coral pixel art sprites", and their choice: the 3D rock kept, each colony a sprite
 * on it), each its form's colony's (`colonyOf`) in its look, standing where the kit's colony stood, as wide as its
 * footprint across (the wider of its form's spans, at its scale) and as tall as its picture is for that width (JUDGED:
 * so a colony covers the rock it covered, its picture's proportions kept); turned over or not by its place, so
 * neighbors of one form differ. One batch for all, faced to the camera as the fish are, drawn solid, hiding what stands
 * behind it as the 3D colonies did; drawn after the rock (`renderOrder` 3, the rock's 2.9, M172) and before every
 * animal, so one swimming in front of a colony is drawn over it (M164's review: the loose colonies had been drawn after
 * the fish, over them).
 */
export class ColonySprites {
  readonly group = new THREE.Group();
  private readonly batch: FishBatch;
  /** Every coral's sprites in one atlas, shared by every set (`coralAtlas`). */
  private readonly atlas = takeCoralAtlas();
  /** Whether it has let the atlas go (`dispose`), so a second `dispose` lets go of none another set holds. */
  private released = false;
  /** Each form's colony's sprites and its span across (model meters). */
  private readonly forms = new Map<string, { sprites: Readonly<Record<CoralLook, Sprite>>; span: number }>();
  private laid: { form: string; look: CoralLook; matrix: THREE.Matrix4; stand?: number }[] = [];
  /** The colonies `done` last showed. */
  private shown: { form: string; look: CoralLook; matrix: THREE.Matrix4; stand?: number }[] = [];
  /** Another set whose colonies this draws among its own, far to near together (`join`, M172). */
  private joined: ColonySprites | null = null;
  /** The ground its colonies stand over (`setGround`, M175): none unless set, each then drawn at its own `stand`. */
  private groundAt: ((x: number, z: number) => number) | null = null;
  /** The limu standing among them (M243, `setPlants`): each its sprite, its foot and its width, m. */
  private plants: { sprite: Sprite; x: number; y: number; z: number; width: number }[] = [];
  /**
   * Each limu's footprint's ground by its foot and width (M244): the floor and the rock under it never move once set
   * (`setGround`), so read once a plant and kept, not at each day's `done` (review M244's: read for 6,201 plants each
   * day, `done` took 48.3 ms against 4.2).
   */
  private plantGround = new Map<string, Float32Array>();

  constructor(forms: ReadonlyMap<string, Part>) {
    this.batch = new FishBatch(this.atlas.texture, true, true);
    this.batch.mesh.renderOrder = 3;
    this.group.add(this.batch.mesh);
    // Their depth at their feet, written just after their pictures (M172, `FishBatch.depthPass`).
    if (this.batch.depthPass) {
      this.batch.depthPass.renderOrder = 3.05;
      this.group.add(this.batch.depthPass);
    }
    for (const [form, part] of forms) {
      const sprites = colonyOf(form);
      if (sprites) this.forms.set(form, { sprites, span: spanOf(part) });
    }
  }

  /**
   * The ground its colonies stand over, the floor and the rock (M175): each colony laid at `done` with its footprint's
   * ground, its middle and two rings of eight at a half and the whole of its half-width (`FishDraw.ground`), so its
   * picture is drawn, from wherever the camera is, at the nearest of it, in place of its `stand`.
   */
  setGround(groundAt: (x: number, z: number) => number): void {
    this.groundAt = groundAt;
    this.plantGround.clear();
  }

  /**
   * Its colonies ordered and stood for a camera before a frame is drawn (M175's review: the batch's own ordering, as each
   * mesh is drawn, comes after three has sent its attributes, so what it writes shows a frame late; the scene's
   * `onBeforeRender`, before they are sent, calls this).
   */
  prepare(camera: THREE.Camera): void {
    this.batch.sortFor(camera);
  }

  /** Every colony taken away, to be laid again. */
  clear(): void {
    this.laid = [];
  }

  /** A colony of a form in a look, where its matrix puts it, drawn `stand` of its width toward the camera (`FishDraw.stand`, M172). */
  add(form: string, look: CoralLook, matrix: THREE.Matrix4, stand = 0): void {
    if (this.forms.has(form)) this.laid.push({ form, look, matrix: matrix.clone(), ...(stand > 0 ? { stand } : {}) });
  }

  /**
   * Another set's colonies drawn in this one's batch, among its own and ordered far to near with them, its own batch
   * hidden; laid again at this one's `done`, so the other's `done` comes first (M172's second review: each set's
   * pictures writing no depth, a farther colony in a set drawn after another's had been drawn over a nearer one of it).
   */
  join(other: ColonySprites): void {
    this.joined = other;
    other.group.visible = false;
  }

  /**
   * The limu drawn among the colonies (M243), far to near with them and hiding and hidden as they are, each its sprite
   * (one of `LIMU_SPRITES`') at its foot, as wide as given; shown at the next `done`, kept out of `drawn`. Each drawn at
   * its footprint's nearest ground, as a colony is (M175; its first draft's at its foot, 37 of the suite's plants cut by
   * the floor in front of them), that ground read once a plant (`plantGround`).
   */
  setPlants(plants: readonly { sprite: Sprite; x: number; y: number; z: number; width: number }[]): void {
    this.plants = plants.slice();
  }

  /** A plant's footprint's ground, read once (`plantGround`). */
  private groundOfPlant(x: number, z: number, width: number): Float32Array {
    const key = `${x},${z},${width}`;
    let ground = this.plantGround.get(key);
    if (!ground && this.groundAt) {
      ground = footprintGround(x, z, width, this.groundAt);
      this.plantGround.set(key, ground);
    }
    return ground as Float32Array;
  }

  /** The limu `setPlants` gave, as the tests read them. */
  plantsLaid(): readonly { sprite: Sprite; x: number; y: number; z: number; width: number }[] {
    return this.plants;
  }

  /** The colonies laid since `clear` shown, with a joined set's last shown, and the limu among them. */
  done(): void {
    const [at, q, scale] = [new THREE.Vector3(), new THREE.Quaternion(), new THREE.Vector3()];
    const rects = this.atlas.rects;
    this.shown = this.laid;
    this.batch.set([...this.laid, ...(this.joined?.shown ?? [])].filter((c) => this.forms.has(c.form)).map((c): FishDraw => {
      const f = this.forms.get(c.form) as { sprites: Readonly<Record<CoralLook, Sprite>>; span: number };
      const sprite = f.sprites[c.look];
      c.matrix.decompose(at, q, scale);
      const width = f.span * Math.max(scale.x, scale.z);
      const right = hash3(Math.round(at.x * 100), Math.round(at.z * 100), 164) < 0.5;
      return { x: at.x, y: at.y, z: at.z, width, height: (width * sprite.height) / sprite.width, rect: rects.get(sprite) as [number, number, number, number], right, alpha: 1, ...(c.stand ? { stand: c.stand } : {}), ...(this.groundAt ? { ground: footprintGround(at.x, at.z, width, this.groundAt) } : {}) };
    }).concat(this.plants.map((p): FishDraw => ({ x: p.x, y: p.y, z: p.z, width: p.width, height: (p.width * p.sprite.height) / p.sprite.width, rect: rects.get(p.sprite) as [number, number, number, number], right: hash3(Math.round(p.x * 100), Math.round(p.z * 100), 243) < 0.5, alpha: 1, ...(this.groundAt ? { ground: this.groundOfPlant(p.x, p.z, p.width) } : {}) }))));
  }

  /** Each colony `done` last showed, its form, its look and its matrix, as the tests read them. */
  drawn(): { form: string; look: CoralLook; matrix: THREE.Matrix4 }[] {
    return this.shown.map((c) => ({ ...c, matrix: c.matrix.clone() }));
  }

  /** The sprites as the batch draws them, for the tests. */
  draws(): FishDraw[] {
    return this.batch.drawn();
  }

  /** The light they are seen by, each channel 0 to 1, as the rock's. */
  setLight(r: number, g: number, b: number): void {
    this.batch.setLight(r, g, b);
  }

  /** The light they are seen by now, as the tests read it. */
  light(): [number, number, number] {
    return ((this.batch.mesh.material as THREE.ShaderMaterial).uniforms.uLight?.value as THREE.Color).toArray() as [number, number, number];
  }

  /** The water they are seen through, the floor's fog (`FishBatch.fogUniforms`), for their owner to set. */
  fogUniforms(): Partial<FogUniforms> {
    return this.batch.fogUniforms();
  }

  /** Frees its batch, and lets the atlas go (freed with the last set to hold it). */
  dispose(): void {
    this.batch.dispose(false);
    if (this.released) return;
    this.released = true;
    releaseCoralAtlas();
  }
}

/** Where an outcrop stands: its reef column (and its place in `reef.columns`), its foot, its floor's normal, its model and its turn. */
export interface OutcropPlace {
  column: number;
  j: number;
  at: THREE.Vector3;
  up: THREE.Vector3;
  variant: number;
  yaw: number;
}

/**
 * Where a reef's outcrops stand: on each island, one for `km2PerOutcrop` (`KM2_PER_OUTCROP` on the map)
 * of its floor (at least one where it has any), at its columns in the order
 * of their fixed draws, each kept `SPACING` widths from every other, its
 * own island's and its neighbors', where the island has room, and none on a
 * column whose floor stands above the sea's surface, `surfaceY`, and on one steeper than an outcrop leans
 * (`MAX_LEAN_DEG`) only where the island has no other room. `floor(column)` gives the floor's point and normal at
 * a column's middle. `clustered` (M105, the exhibit's), the columns taken by their nearness to a few of them, one for
 * each `CLUSTER_OUTCROPS` outcrops, so the outcrops gather in clusters with sand between, as many as before.
 */
export function outcropPlaces(
  reef: Reef,
  floorKm2: readonly number[],
  floor: (column: number) => { at: THREE.Vector3; up: THREE.Vector3 },
  widthUnits: number,
  outcrops: number,
  surfaceY: number,
  km2PerOutcrop = KM2_PER_OUTCROP,
  clustered = false,
): OutcropPlace[] {
  const places: OutcropPlace[] = [];
  REEF_ISLANDS.forEach((_, isl) => {
    const js = reef.columns.map((_c, j) => j).filter((j) => reef.island[j] === isl);
    const km2 = js.reduce((s, j) => s + (floorKm2[j] ?? 0), 0);
    if (km2 <= 0) return;
    const n = Math.max(1, Math.round(km2 / km2PerOutcrop));
    let order = js.slice().sort((a, b) => hash3(reef.columns[a] as number, 0, 71) - hash3(reef.columns[b] as number, 0, 71));
    if (clustered) {
      // The clusters' middles the first of the draws; each column then by its distance to the nearest, a little of its
      // own draw breaking ties, so each cluster fills outward from its middle.
      const middles = order.slice(0, Math.ceil(n / CLUSTER_OUTCROPS)).map((j) => floor(reef.columns[j] as number).at);
      const near = new Map(js.map((j) => {
        const at = floor(reef.columns[j] as number).at;
        return [j, Math.min(...middles.map((m) => Math.hypot(m.x - at.x, m.z - at.z))) + widthUnits * hash3(reef.columns[j] as number, 1, 71)] as const;
      }));
      order = order.slice().sort((a, b) => (near.get(a) as number) - (near.get(b) as number));
    }
    // Its columns no steeper than an outcrop may lean first, the steeper only where the island has no other room
    // (M110: the kit's wide outcrops, their rubble spread round them, were buried whole on two of the exhibit's
    // columns of 50° and 62°, sunk 0.37 and 0.15 units against heights of 0.12 and 0.08, MEASURED).
    const steep = (j: number): boolean => floor(reef.columns[j] as number).up.angleTo(new THREE.Vector3(0, 1, 0)) > (MAX_LEAN_DEG * Math.PI) / 180;
    order = [...order.filter((j) => !steep(j)), ...order.filter(steep)];
    const mine: OutcropPlace[] = [];
    for (const j of order) {
      if (mine.length === n) break;
      const column = reef.columns[j] as number;
      const { at, up } = floor(column);
      // None on a shore column whose floor, as drawn, stands above the sea (M87's review: seven did on the map).
      if (at.y >= surfaceY) continue;
      if ([...places, ...mine].some((p) => Math.hypot(p.at.x - at.x, p.at.z - at.z) < SPACING * widthUnits)) continue;
      mine.push({ column, j, at, up, variant: Math.floor(hash3(column, 0, 72) * outcrops), yaw: 2 * Math.PI * hash3(column, 0, 73) });
    }
    places.push(...mine);
  });
  return places;
}

/** How far apart outcrops stand, in the widest one's width, middle to middle (JUDGED: a little room between them, and room for all of Molokaʻi's 21 in the exhibit's band, `EXHIBIT_SPRITES`). */
const SPACING = 1.1;

/** Outcrops a cluster, where they are clustered (M105, JUDGED: the redesign v2's "several prominent coral-covered outcrop clusters", "sand channels and gaps"). */
const CLUSTER_OUTCROPS = 4;

/**
 * How far an outcrop leans toward its floor's slope, degrees at most (JUDGED: upright on the diorama's steep
 * slope one was half buried, and wholly tilted on the map's, drawn 75 times steeper than it is, M87's review
 * found them leaning 23° to 83°).
 */
const MAX_LEAN_DEG = 40;

/** An outcrop's matrix: at its place, leaned toward its floor's slope by `MAX_LEAN_DEG` at most, turned by its yaw, at a scale. */
function placeMatrix(p: OutcropPlace, scale: number): THREE.Matrix4 {
  const up = new THREE.Vector3(0, 1, 0);
  const tilt = Math.min(up.angleTo(p.up), (MAX_LEAN_DEG * Math.PI) / 180);
  const axis = new THREE.Vector3().crossVectors(up, p.up);
  const lean = axis.lengthSq() > 1e-12 ? new THREE.Quaternion().setFromAxisAngle(axis.normalize(), tilt) : new THREE.Quaternion();
  const turn = new THREE.Quaternion().setFromAxisAngle(up, p.yaw);
  return new THREE.Matrix4().compose(p.at, lean.multiply(turn), new THREE.Vector3(scale, scale, scale));
}

/** The share of an outcrop's height above its lowest point that its base is taken to be (JUDGED: its foot's ring of points). */
const BASE_SHARE = 0.05;

/**
 * The outcrops sunk into the floor (M104, the brief's "outcrops embedded in continuous substrate"): each lowered by
 * the most any point of its base, as placed, stands above `floorY` under it, so none stands clear of its slope (5 of
 * Maui Nui's 41 in the exhibit had, by up to 0.11 units, a sixth of their width); their uphill sides go into it. One
 * that would be sunk more than `BURIED_SHARE` of its height stands as the model's outcrop sunk least for its height
 * there instead (M110: the kit's wide outcrops spread their rubble round them, flat, and where the exhibit's floor
 * bends under so wide a foot three of Maui Nui's 41 were sunk about their whole height, MEASURED); unless `refit` is
 * false, where the places were chosen so (the Reef's, `reefOutcropPlaces`).
 */
export function seatedPlaces(model: ReefModel, places: readonly OutcropPlace[], widthUnits: number, floorY: (x: number, z: number) => number, refit = true): OutcropPlace[] {
  const scale = widthUnits / Math.max(...model.outcrops.map((o) => o.widthM));
  return places.map((p) => {
    let { gap, height } = outcropSeat(model, p, p.variant, scale, floorY);
    let variant = p.variant;
    if (refit && gap > BURIED_SHARE * height) {
      const best = model.outcrops.map((_, k) => ({ k, ...outcropSeat(model, p, k, scale, floorY) })).reduce((a, b) => (b.gap / b.height < a.gap / a.height ? b : a));
      [variant, gap] = [best.k, best.gap];
    }
    return { ...p, variant, at: p.at.clone().setY(p.at.y - gap) };
  });
}

/** How far an outcrop of a variant must sink at a place to leave no point of its base clear of the floor, and its height, scene units. */
export function outcropSeat(model: ReefModel, p: OutcropPlace, variant: number, scale: number, floorY: (x: number, z: number) => number): { gap: number; height: number } {
  const pos = (model.outcrops[variant] as ReefModel['outcrops'][number]).rock.position;
  let [lo, hi] = [Infinity, -Infinity];
  for (let k = 1; k < pos.length; k += 3) [lo, hi] = [Math.min(lo, pos[k] as number), Math.max(hi, pos[k] as number)];
  const m = placeMatrix({ ...p, variant }, scale);
  const v = new THREE.Vector3();
  let gap = 0;
  for (let k = 0; k < pos.length; k += 3) {
    if ((pos[k + 1] as number) > lo + BASE_SHARE * (hi - lo)) continue;
    v.set(pos[k] as number, pos[k + 1] as number, pos[k + 2] as number).applyMatrix4(m);
    gap = Math.max(gap, v.y - floorY(v.x, v.z));
  }
  return { gap, height: (hi - lo) * scale };
}

/** The most of its height an outcrop is sunk before the model's outcrop sunk least for its height stands in its place (JUDGED: half of it shows). */
export const BURIED_SHARE = 0.5;

/** Cells a side of each outcrop's height field (`ReefOutcrops.solidAt`; JUDGED: a cell about a quarter meter of the model's, its triangles' size). */
const SOLID_CELLS = 24;

/** Parts' corners placed by their matrices, x, y, z in threes, in one array (M118: built a number at a time, 99 outcrops' had taken 0.4 s in node). */
function placedCorners(list: readonly { part: Part | undefined; m: THREE.Matrix4 }[]): Float64Array {
  const v = new THREE.Vector3();
  const corners = new Float64Array(list.reduce((n, { part }) => n + (part?.position.length ?? 0), 0));
  let at3 = 0;
  for (const { part, m } of list) {
    for (let k = 0; part && k < part.position.length; k += 3, at3 += 3) {
      v.fromArray(part.position, k).applyMatrix4(m);
      [corners[at3], corners[at3 + 1], corners[at3 + 2]] = [v.x, v.y, v.z];
    }
  }
  return corners;
}

/**
 * Corners' triangles (nine numbers each) laid on a square of `SOLID_CELLS` cells a side over their box across: the
 * square's corner and its cells' width, and `onCell` told each cell a triangle's corners span (the triangle by its
 * first number's index).
 */
function laidOnCells(corners: Float64Array, onCell: (cell: number, k: number) => void): { x0: number; z0: number; cell: number } {
  let [x0, x1, z0, z1] = [Infinity, -Infinity, Infinity, -Infinity];
  for (let k = 0; k < corners.length; k += 3) {
    [x0, x1] = [Math.min(x0, corners[k] as number), Math.max(x1, corners[k] as number)];
    [z0, z1] = [Math.min(z0, corners[k + 2] as number), Math.max(z1, corners[k + 2] as number)];
  }
  const cell = Math.max(x1 - x0, z1 - z0, 1e-6) / SOLID_CELLS;
  const at = (u: number): number => Math.min(SOLID_CELLS - 1, Math.max(0, Math.floor(u / cell)));
  for (let k = 0; k + 8 < corners.length; k += 9) {
    const [ax, bx, cx] = [corners[k] as number, corners[k + 3] as number, corners[k + 6] as number];
    const [az, bz, cz] = [corners[k + 2] as number, corners[k + 5] as number, corners[k + 8] as number];
    const [kx0, kx1] = [at(Math.min(ax, bx, cx) - x0), at(Math.max(ax, bx, cx) - x0)];
    for (let kz = at(Math.min(az, bz, cz) - z0); kz <= at(Math.max(az, bz, cz) - z0); kz++) {
      for (let kx = kx0; kx <= kx1; kx++) onCell(kz * SOLID_CELLS + kx, k);
    }
  }
  return { x0, z0, cell };
}

/**
 * Maui Nui Reef's rock is laid in chunks (`ReefScene`'s, `tiles` of `ReefOutcrops`), its outcrops by a grid of
 * `ROCK_TILES` a side over their spread, each chunk a mesh of its own with its own bounds, so what is out of view is
 * culled and a ray tests only the chunks it passes (review M122: one mesh of its 120 outcrops, 507,000 triangles, had
 * been drawn whole every frame, about a third of them in a phone's home view, and tested whole by every zoom's aim, 30 to
 * 71 ms). JUDGED: 10, its rock 46 chunks, a phone's home view drawing 156,077 of its 506,732 triangles in 10 of them and
 * an aim taking 11 to 13 ms (MEASURED in node under another run's load, as the 30 to 71 were); 6 drew 210,836 in larger
 * chunks the aim's ray passed, 14 drew 141,706 in 64. The whole tank and the map keep one mesh: their outcrops in view at
 * once, chunks would cull nothing and draw the rock's two sides in some 70 draws for 2 (review M122's last reviewer).
 */
export const ROCK_TILES = 10;

export class ReefOutcrops {
  readonly group = new THREE.Group();
  /** The rock's material (the Reef scene's loose rocks share it). */
  readonly materials: { rock: THREE.MeshStandardMaterial };
  /** Its colonies, the user's sprites (M164). */
  readonly colonies: ColonySprites;
  /** The rock, in its chunks (`tiles` a side). */
  private readonly rocks: THREE.Mesh[];
  /** Each outcrop's matrix, from the model's meters to the scene. */
  private readonly matrices: THREE.Matrix4[];
  /** Which colonies were last drawn, and how each looked; and which stood. */
  private drawn = '';
  private standing = '';
  /** How many colonies stand now, which, and how each looks. */
  shown: { place: number; slot: number; look: CoralLook }[] = [];
  /** Each outcrop's solid as a height field (`solidAt`) and its top, built when first asked after the colonies change. */
  private solids: { x0: number; z0: number; cell: number; foot: number; tops: Float32Array; highest: number }[] | null = null;
  /**
   * The rock alone, its colonies left out (M175: what a colony stands over and must stand clear of), built once: each
   * outcrop's triangles' corners and, for each cell of its square, the triangles over it, so `rockTopAt` reads the rock's
   * own height (M175's review, MEASURED by rays cast down onto the rock: the height field's cells, each its triangles'
   * highest corner, had read 0.11 m over the rock at the median under the outcrops' colonies' footprints, 0.59 m at most;
   * their own heights' highest within each cell, 0.10 and 0.51 m; now none).
   */
  private rock: { x0: number; z0: number; cell: number; corners: Float64Array; cells: number[][] }[] | null = null;
  /** Each material's own color (plain until the skins come, then white under them), and the light they are seen by. */
  private base: THREE.Color[];
  private light: [number, number, number] = [1, 1, 1];

  /** @param widthUnits how wide the model's widest outcrop is drawn, scene units; the rest to its scale */
  /** @param tiles the rock's chunks a side (`ROCK_TILES`), one mesh by default */
  constructor(private readonly model: ReefModel, readonly places: readonly OutcropPlace[], widthUnits: number, tiles = 1) {
    const side = (both: boolean): THREE.Side => (both ? THREE.DoubleSide : THREE.FrontSide);
    // Drawn after the water and its surface, as the corals' sprites were (M85), so the water's tint and its ripple
    // do not wash them out: in three.js's later, transparent pass, wholly opaque, writing depth. Each in a plain color
    // near its skin's until the skins come.
    const made = (color: number, both: boolean): THREE.MeshStandardMaterial => new THREE.MeshStandardMaterial({ color, roughness: 1, metalness: 0, side: side(both), transparent: true });
    this.materials = { rock: made(0x66788f, model.doubleSided.rock) };
    this.base = this.all().map((m) => m.color.clone());
    const scale = widthUnits / Math.max(...model.outcrops.map((o) => o.widthM));
    this.matrices = places.map((p) => placeMatrix(p, scale));
    // The rock never changes: laid once, in `tiles` chunks a side, each place's in the tile of the grid over the places'
    // spread its middle falls in, in the places' order.
    const [xs, zs] = [places.map((p) => p.at.x), places.map((p) => p.at.z)];
    const [x0, z0] = [Math.min(...xs), Math.min(...zs)];
    const [dx, dz] = [Math.max(...xs) - x0 || 1, Math.max(...zs) - z0 || 1];
    const tileOf = (u: number, span: number): number => Math.min(tiles - 1, Math.floor((u / span) * tiles));
    const chunks = new Map<number, number[]>();
    places.forEach((p, i) => {
      const key = tileOf(p.at.z - z0, dz) * tiles + tileOf(p.at.x - x0, dx);
      chunks.set(key, [...(chunks.get(key) ?? []), i]);
    });
    this.rocks = [...chunks.values()].map((list) => new THREE.Mesh(partGeometry(laidPart(list.map((i) => ({ part: (model.outcrops[places[i]?.variant ?? 0] as ReefModel['outcrops'][number]).rock, matrix: this.matrices[i] as THREE.Matrix4 })))), this.materials.rock));
    // The colonies the user's sprites (M164), drawn just after the rock (their `renderOrder` 3).
    this.colonies = new ColonySprites(model.forms);
    // Before every colony's picture (M172's second review: the pictures writing no depth, a rock drawn between a picture
    // and its depth, by three's sort among things of one order, had painted over the colonies before it).
    for (const mesh of this.rocks) mesh.renderOrder = 2.9;
    this.group.add(...this.rocks, this.colonies.group);
  }

  private all(): THREE.MeshStandardMaterial[] {
    return [this.materials.rock];
  }

  /** The rock's meshes, its chunks (M177: Maui Nui Reef paints them as its floor's hard bottom). */
  rockMeshes(): readonly THREE.Mesh[] {
    return this.rocks;
  }

  /** The rock's skin, once the browser has read the model's image; until then it is drawn in a plain color. */
  setTextures(textures: ReefTextures): void {
    this.materials.rock.map = textures.rock;
    for (const m of this.all()) m.needsUpdate = true;
    this.base = this.base.map(() => new THREE.Color(0xffffff));
    this.setLight(...this.light);
  }

  /** The light they are seen by, each channel 0 to 1, as the fish's (M104: they had kept the day's light at night). */
  setLight(r: number, g: number, b: number): void {
    this.light = [r, g, b];
    this.colonies.setLight(r, g, b);
    this.all().forEach((m, i) => {
      m.color.copy(this.base[i] as THREE.Color).multiply(new THREE.Color(r, g, b));
    });
  }

  /**
   * The colonies for each reef column's cover, its share bleached and its share a heat episode under way has killed,
   * percent, in `reef.columns`' order (`colonyLook`): each standing while its draw is under the cover's share of
   * `fullPercent`, the reef's ceiling unless said (M125: Maui Nui Reef's, the cover every colony it draws makes).
   */
  setCover(coverPercent: readonly number[], bleachedPercent: readonly number[], killedPercent: readonly number[] = [], fullPercent: number = CORAL_RATES.ceilingPercent): void {
    const shown: ReefOutcrops['shown'] = [];
    this.places.forEach((p, i) => {
      const share = Math.min(1, (coverPercent[p.j] ?? 0) / fullPercent);
      this.model.outcrops[p.variant]?.slots.forEach((_s, k) => {
        const look = colonyLook(hash3(p.column, k, 81), hash3(p.column, k, 82), share, bleachedPercent[p.j] ?? 0, killedPercent[p.j] ?? 0);
        if (look) shown.push({ place: i, slot: k, look });
      });
    });
    const key = shown.map((s) => `${s.place}.${s.slot}${s.look}`).join(',');
    if (key === this.drawn) return;
    this.drawn = key;
    // The height fields only when which colonies stand changes, not their looks (the review's: a look's change alone
    // had rebuilt them all, 40 to 320 ms in node, on the next frame the camera was held).
    const standing = shown.map((s) => `${s.place}.${s.slot}`).join(',');
    if (standing !== this.standing) {
      this.standing = standing;
      this.solids = null;
    }
    this.shown = shown;
    this.colonies.clear();
    const m = new THREE.Matrix4();
    for (const s of shown) {
      const slot = this.model.outcrops[this.places[s.place]?.variant ?? 0]?.slots[s.slot];
      // Drawn toward the camera off their rock (M172: on its slopes the rock about each one's foot had cut its picture).
      if (slot) this.colonies.add(slot.form, s.look, m.multiplyMatrices(this.matrices[s.place] as THREE.Matrix4, slot.matrix), SOLID_STAND);
    }
    this.colonies.done();
  }

  /**
   * Whether a point in the scene is inside an outcrop, its rock or a colony on it now (M105, for
   * `ExhibitView.shotComposition`): each outcrop taken as a height field over `SOLID_CELLS` cells a side of its square,
   * each cell as high as the highest corner of the triangles over it, from its foot up; an overhang counts as solid.
   */
  solidAt(p: THREE.Vector3): boolean {
    this.solids ??= this.solidsOf();
    for (const s of this.solids) {
      const i = Math.floor((p.x - s.x0) / s.cell);
      const k = Math.floor((p.z - s.z0) / s.cell);
      if (i < 0 || k < 0 || i >= SOLID_CELLS || k >= SOLID_CELLS || p.y < s.foot) continue;
      if (p.y <= (s.tops[k * SOLID_CELLS + i] as number)) return true;
    }
    return false;
  }

  /**
   * The top of the outcrops standing over a point (x, z), scene units, as `solidAt` reads them; -Infinity where none
   * does (M110: a habitat scene's camera and fish kept above them).
   */
  topAt(x: number, z: number): number {
    this.solids ??= this.solidsOf();
    let top = -Infinity;
    for (const s of this.solids) {
      const i = Math.floor((x - s.x0) / s.cell);
      const k = Math.floor((z - s.z0) / s.cell);
      if (i < 0 || k < 0 || i >= SOLID_CELLS || k >= SOLID_CELLS) continue;
      top = Math.max(top, s.tops[k * SOLID_CELLS + i] as number);
    }
    return top;
  }

  /** The top of the outcrops' rock alone over a point (x, z), its colonies left out (M175); -Infinity where none stands. */
  rockTopAt(x: number, z: number): number {
    this.rock ??= this.rockOf();
    let top = -Infinity;
    for (const r of this.rock) {
      const i = Math.floor((x - r.x0) / r.cell);
      const k = Math.floor((z - r.z0) / r.cell);
      if (i < 0 || k < 0 || i >= SOLID_CELLS || k >= SOLID_CELLS) continue;
      const c = r.corners;
      for (const t of r.cells[k * SOLID_CELLS + i] as number[]) {
        // The triangle's height over (x, z) by its corners' weights, where (x, z) lies on it.
        const [ax, ay, az] = [c[t] as number, c[t + 1] as number, c[t + 2] as number];
        const [bx, by, bz] = [c[t + 3] as number, c[t + 4] as number, c[t + 5] as number];
        const [cx, cy, cz] = [c[t + 6] as number, c[t + 7] as number, c[t + 8] as number];
        const area = (bx - ax) * (cz - az) - (cx - ax) * (bz - az);
        if (Math.abs(area) < 1e-12) continue;
        const wb = ((x - ax) * (cz - az) - (cx - ax) * (z - az)) / area;
        const wc = ((bx - ax) * (z - az) - (x - ax) * (bz - az)) / area;
        if (wb >= -1e-9 && wc >= -1e-9 && wb + wc <= 1 + 1e-9) top = Math.max(top, ay + wb * (by - ay) + wc * (cy - ay));
      }
    }
    return top;
  }

  /** Each outcrop's rock's triangles, placed, and the triangles over each cell of its square (`rock`). */
  private rockOf(): NonNullable<ReefOutcrops['rock']> {
    return this.places.map((p, i) => {
      const corners = placedCorners([{ part: this.model.outcrops[p.variant]?.rock, m: this.matrices[i] as THREE.Matrix4 }]);
      const cells: number[][] = Array.from({ length: SOLID_CELLS * SOLID_CELLS }, () => []);
      return { ...laidOnCells(corners, (c, k) => (cells[c] as number[]).push(k)), corners, cells };
    });
  }

  /** The highest the outcrops stand anywhere, scene units, as `topAt` reads them; -Infinity where none does. */
  highest(): number {
    this.solids ??= this.solidsOf();
    return this.solids.reduce((top, s) => Math.max(top, s.highest), -Infinity);
  }

  /** Each outcrop's height field (`solidAt`): its rock's and its shown colonies' triangles (each as tall as its sprite, M164) laid on its square's cells. */
  private solidsOf(): NonNullable<ReefOutcrops['solids']> {
    const parts = this.places.map((p, i) => [{ part: this.model.outcrops[p.variant]?.rock, m: this.matrices[i] as THREE.Matrix4 }]);
    for (const s of this.shown) {
      const slot = this.model.outcrops[this.places[s.place]?.variant ?? 0]?.slots[s.slot];
      const part = slot && this.model.forms.get(slot.form);
      // Each colony as tall as its sprite stands (M164, `colonyStretch`).
      if (slot && part) parts[s.place]?.push({ part, m: new THREE.Matrix4().multiplyMatrices(this.matrices[s.place] as THREE.Matrix4, slot.matrix).multiply(stretchedForm(slot.form, part)) });
    }
    return parts.map((list) => {
      const corners = placedCorners(list);
      let foot = Infinity;
      for (let k = 1; k < corners.length; k += 3) foot = Math.min(foot, corners[k] as number);
      const tops = new Float32Array(SOLID_CELLS * SOLID_CELLS).fill(-Infinity);
      // Each triangle raised over the cells its corners span, to its highest corner.
      const square = laidOnCells(corners, (c, k) => { tops[c] = Math.max(tops[c] as number, corners[k + 1] as number, corners[k + 4] as number, corners[k + 7] as number); });
      return { ...square, foot, tops, highest: tops.reduce((m, h) => Math.max(m, h), -Infinity) };
    });
  }

  /** Each colony drawn now, its form, its look and its matrix, as the tests read them. */
  drawnColonies(): { form: string; look: CoralLook; matrix: THREE.Matrix4 }[] {
    return this.colonies.drawn();
  }

  /** The water the colonies are seen through, for their owner to fog as it fogs the rock (M164). */
  colonyFog(): Partial<FogUniforms> {
    return this.colonies.fogUniforms();
  }


  /** Frees its meshes and materials; the skins themselves are the page's, shared by every view (`reefTextures`). */
  dispose(): void {
    for (const rock of this.rocks) rock.geometry.dispose();
    this.colonies.dispose();
    for (const material of this.all()) material.dispose();
  }
}

/**
 * The model's skin as the browser reads it: the rock's (its coral's looks' no longer read, the colonies the user's
 * sprites since M164).
 */
export interface ReefTextures {
  rock: THREE.Texture;
}

/** The model's images as textures, read by the browser: pixel art magnified square, its atlas's cells not wrapped. */
export async function reefTextures(model: ReefModel): Promise<ReefTextures> {
  const texture = async (png: Uint8Array): Promise<THREE.Texture> => {
    const bitmap = await createImageBitmap(new Blob([png as BlobPart], { type: 'image/png' }));
    const t = new THREE.Texture(bitmap);
    // glTF's UVs run from the image's top row.
    t.flipY = false;
    t.colorSpace = THREE.SRGBColorSpace;
    t.magFilter = THREE.NearestFilter;
    t.minFilter = THREE.LinearMipmapLinearFilter;
    t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
    t.needsUpdate = true;
    return t;
  };
  return { rock: await texture(model.images.rock) };
}
